/**
 * StaleIndicator — Universal stale/disconnected marker for live data.
 *
 * Shows a subtle indicator when:
 *   - WebSocket is disconnected (reconnecting state)
 *   - A symbol hasn't received a tick in >5s during market hours
 *   - Data is from snapshot/cache (not live WS)
 *
 * Usage:
 *   <StaleIndicator isConnected={true} isStale={false} />
 *   <StaleIndicator isConnected={false} />
 */
import React from 'react';
import { Wifi, WifiOff } from 'lucide-react';

export function StaleIndicator({ isConnected, isStale, showLabel = false, size = 'sm' }) {
  if (isConnected && !isStale) return null;

  const sizeClasses = {
    xs: 'w-2 h-2',
    sm: 'w-3 h-3',
    md: 'w-4 h-4',
  };

  const iconSize = size === 'xs' ? 8 : size === 'sm' ? 12 : 16;

  if (!isConnected) {
    return (
      <span
        className="inline-flex items-center gap-1 text-amber-500"
        title="Reconnecting to live feed..."
        data-testid="stale-indicator-disconnected"
      >
        <WifiOff className={sizeClasses[size]} size={iconSize} />
        {showLabel && <span className="text-xs">Reconnecting</span>}
      </span>
    );
  }

  if (isStale) {
    return (
      <span
        className="inline-flex items-center gap-1 text-amber-400"
        title="Price may be delayed"
        data-testid="stale-indicator-stale"
      >
        <span className={`${sizeClasses[size]} rounded-full bg-amber-400 animate-pulse`} />
        {showLabel && <span className="text-xs">Delayed</span>}
      </span>
    );
  }

  return null;
}

/**
 * LiveDot — Tiny green dot indicating live WS connection.
 */
export function LiveDot({ isConnected, size = 'xs' }) {
  if (!isConnected) return null;
  const sizeClasses = { xs: 'w-1.5 h-1.5', sm: 'w-2 h-2', md: 'w-3 h-3' };
  return (
    <span
      className={`${sizeClasses[size]} rounded-full bg-emerald-500 inline-block`}
      title="Live"
      data-testid="live-dot"
    />
  );
}

/**
 * ConnectionBanner — Shows a banner when WS is disconnected.
 * Place at the top of pages that rely on live data.
 * Waits 3s before showing to avoid flash during initial connection.
 */
export function ConnectionBanner({ isConnected, streamStatus }) {
  const [showBanner, setShowBanner] = React.useState(false);

  React.useEffect(() => {
    if (isConnected) {
      setShowBanner(false);
      return;
    }
    // Delay showing the banner to avoid brief flashes on page load
    const timer = setTimeout(() => setShowBanner(true), 3000);
    return () => clearTimeout(timer);
  }, [isConnected]);

  if (!showBanner) return null;

  return (
    <div
      className="bg-amber-50 border-b border-amber-200 px-4 py-1.5 text-center text-xs text-amber-700 flex items-center justify-center gap-2"
      data-testid="connection-banner"
    >
      <WifiOff className="w-3 h-3" />
      <span>
        {streamStatus === 'reconnecting'
          ? 'Reconnecting to live feed... Showing last known prices.'
          : 'Live feed unavailable. Showing cached data.'}
      </span>
    </div>
  );
}

/**
 * LiveStatusBadge — Compact "Live" / "Delayed" badge for page headers.
 */
export function LiveStatusBadge({ isConnected }) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 text-xs font-medium px-2 py-0.5 rounded-full ${
        isConnected ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700'
      }`}
      data-testid="live-status-badge"
    >
      <span className={`w-1.5 h-1.5 rounded-full ${
        isConnected ? 'bg-green-500 animate-pulse' : 'bg-amber-500'
      }`} />
      {isConnected ? 'Live' : 'Delayed'}
    </span>
  );
}

export default StaleIndicator;
